// The report plan proposed before production: the report's blocks mapped to
// the previous report, the conflicts to arbitrate and the proposed changes.
// Decisions change what the agents read; validation needs every item decided.
import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, CheckCircle2, X } from "lucide-react";
import { api } from "../api/client";
import { invalidate, useMutation, useResource } from "../api/resource";
import type { PlanItem } from "../api/types";
import { ErrorState, Notice, PageHeader, SkeletonBlock, StatusBadge } from "../components/ui";
import { useCurrentUser } from "../auth";
import { keys, useDossier } from "../dossier";
import { formatDateTime, locale, w } from "../i18n";
import type { UserStatus } from "../status";

const MAPPING_STATUS: Record<string, UserStatus> = { same: "ready", renumbered: "partial", title_differs: "needs_review",
  absent: "missing", none: "not_started" };
const SEVERITY_STATUS: Record<string, UserStatus> = { high: "failed", medium: "needs_review", low: "not_started" };

function ItemCard({ item, onDecide, busy }: { item: PlanItem; onDecide: (id: string, decision: "accepted" | "rejected") => void; busy: boolean }) {
  const { can } = useCurrentUser();
  busy = busy || !can("reviewer");
  const decided = item.decision?.decision;
  return <li className={`ws-plan-item ${item.type} ${item.severity}`}>
    <div className="ws-row" style={{ justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
      <div>
        <strong>{item.message}</strong>
        <div className="ws-small ws-muted" style={{ marginTop: 2 }}>{item.hint}</div>
        {item.decision && <div className="ws-small ws-faint" style={{ marginTop: 4 }}>
          {w(`plan.decided.${item.decision.decision}`, { actor: item.decision.actor, date: formatDateTime(item.decision.at) })}</div>}
      </div>
      <StatusBadge status={SEVERITY_STATUS[item.severity]} label={w(`plan.severity.${item.severity}`)} />
    </div>
    <div className="ws-row" style={{ marginTop: 8, gap: 6 }}>
      {item.code && <Link className="ws-btn sm ghost" to={item.code.includes(".") ? `../sections/${item.code}` : `../chapters/${item.code}`}
        relative="path">{item.code}</Link>}
      <span style={{ flex: 1 }} />
      <button className={`ws-btn sm${decided === "accepted" ? " primary" : ""}`} disabled={busy || decided === "accepted"}
        onClick={() => onDecide(item.id, "accepted")}><Check size={14} />{w(item.type === "conflict" ? "plan.acknowledge" : "plan.accept")}</button>
      {item.type === "proposal" && <button className={`ws-btn sm${decided === "rejected" ? " danger" : ""}`} disabled={busy || decided === "rejected"}
        onClick={() => onDecide(item.id, "rejected")}><X size={14} />{w("plan.reject")}</button>}
    </div>
  </li>;
}

export function PlanPage() {
  const { dossier } = useDossier();
  const lang = locale();
  const plan = useResource(keys.plan(dossier.id, lang), () => api.plan(dossier.id, lang));
  const decide = useMutation(api.decidePlanItem);
  const validate = useMutation(api.validatePlan);
  const [filter, setFilter] = useState<"all" | "pending">("pending");
  const data = plan.data;
  const refresh = () => invalidate(`plan:${dossier.id}`, keys.audit(dossier.id), "inputs:", "sources:");
  const onDecide = async (id: string, decision: "accepted" | "rejected") => {
    if (await decide.run(dossier.id, id, decision)) refresh();
  };
  const onValidate = async () => { if (await validate.run(dossier.id)) refresh(); };
  const items = (data?.items || []).filter(item => filter === "all" || !item.decision);
  const { can } = useCurrentUser();

  return <>
    <PageHeader eyebrow={w("plan.eyebrow")} title={w("plan.title")} lead={w("plan.lead")}
      actions={<button className="ws-btn primary" title={can("approver") ? undefined : w("auth.needs", { role: w("role.user.approver") })}
        disabled={!can("approver") || !data || data.counts.pending > 0 || data.status === "validated" || validate.pending}
        onClick={onValidate}><CheckCircle2 size={16} />{w("plan.validate")}</button>} />
    {plan.error && <ErrorState title={w("common.genericError")} error={plan.error} onRetry={plan.reload} />}
    {(decide.error || validate.error) && <ErrorState title={w("plan.saveError")} error={decide.error || validate.error} />}
    {plan.loading && <SkeletonBlock lines={10} />}
    {data && <>
      {data.status === "validated" && <Notice tone="positive" title={w("plan.validatedTitle", { actor: data.validated_by, date: formatDateTime(data.validated_at) })}>
        {w("plan.validatedBody")}</Notice>}
      {data.status === "outdated" && <Notice tone="caution" title={w("plan.outdatedTitle")}>{w("plan.outdatedBody")}</Notice>}
      {data.status === "draft" && data.counts.pending > 0 && <Notice tone="info">{w("plan.pendingBody", { count: data.counts.pending })}</Notice>}
      {!data.has_previous_report && <div style={{ marginTop: 12 }}><Notice tone="neutral">{w("plan.noPrevious")}</Notice></div>}

      <section className="ws-card ws-steps" style={{ marginTop: 16 }} aria-label={w("plan.summary")}>
        {([["blocks", data.counts.blocks], ["kinds", data.counts.kinds], ["conflicts", data.counts.conflicts],
          ["proposals", data.counts.proposals]] as const).map(([key, value]) => <div key={key} className="ws-step">
          <div className="ws-step-value">{value}</div><div className="ws-step-label">{w(`plan.kpi.${key}`)}</div></div>)}
      </section>

      <div className="ws-grid-2" style={{ marginTop: 16, alignItems: "start" }}>
        <section className="ws-card">
          <div className="ws-card-head"><h2>{w("plan.tree")}</h2>
            <span className="ws-small ws-muted">{data.previous_reports.join(", ") || w("plan.noPreviousShort")}</span></div>
          <div className="ws-table-wrap" style={{ maxHeight: 640 }}><table className="ws-table">
            <thead><tr><th>{w("plan.col.block")}</th><th>{w("plan.col.kind")}</th><th>{w("plan.col.previous")}</th></tr></thead>
            <tbody>{data.blocks.map(block => <tr key={block.code}>
              <td style={{ paddingLeft: block.level === 2 ? 24 : 12 }}><span className="ws-mono">{block.code === "SYN" ? "" : block.code}</span>{" "}
                {block.level === 1 ? <strong>{block.title}</strong> : block.title}</td>
              <td className="ws-small ws-muted">{w(`plan.kind.${block.kind}`)}</td>
              <td>{block.previous
                ? <span className="ws-small"><span className="ws-mono">{block.previous.code}</span> {block.previous.title}</span>
                : null}
                {block.mapping !== "same" && <div><StatusBadge status={MAPPING_STATUS[block.mapping]} label={w(`plan.mapping.${block.mapping}`)} subtle /></div>}</td>
            </tr>)}</tbody>
          </table></div>
        </section>

        <section className="ws-card">
          <div className="ws-card-head"><h2>{w("plan.items")}</h2>
            <select className="ws-select" aria-label={w("plan.items")} value={filter} onChange={e => setFilter(e.target.value as "all" | "pending")}>
              <option value="pending">{w("plan.filter.pending")}</option><option value="all">{w("plan.filter.all")}</option></select></div>
          {!items.length && <p className="ws-card-pad ws-muted" style={{ margin: 0 }}>{w(filter === "pending" ? "plan.nothingPending" : "plan.noItems")}</p>}
          <ul className="ws-plan-items">{items.map(item => <ItemCard key={item.id} item={item} onDecide={onDecide} busy={decide.pending} />)}</ul>
        </section>
      </div>
      <div style={{ marginTop: 16 }}><Notice tone="neutral">{w("plan.note")}</Notice></div>
    </>}
  </>;
}
